import type { GeoLocation } from '@smg-real-estate/geo-libs';

import type { PaginatedAgentsResponse } from '@/api/agentMetaApi';

/**
 * Parent geo ids for empty-result fallback: nearest → farthest.
 * Skips zipcode segments; keeps country as a last-resort parent.
 */
export const getParentGeoIds = (location: GeoLocation): string[] => {
  const parentPath = location.parentPaths?.[0];
  if (!parentPath || parentPath.length === 0) {
    return [];
  }

  return parentPath.filter((id) => !id.startsWith('geo-zipcode'));
};

export interface NearestParentWithAgents {
  location: GeoLocation;
  total: PaginatedAgentsResponse['total'];
  agents: PaginatedAgentsResponse['records'];
}

/**
 * Walks parent location ids nearest → farthest and returns the first resolved location
 * whose first-page fetch has `total > 0`.
 */
export async function findNearestParentWithAgents({
  parentLocationIds,
  resolveLocationById,
  fetchFirstPage,
}: {
  parentLocationIds: string[];
  resolveLocationById: (geoLocationId: string) => GeoLocation | undefined;
  fetchFirstPage: (geoLocationId: string) => Promise<PaginatedAgentsResponse>;
}): Promise<NearestParentWithAgents | null> {
  for (const parentLocationId of parentLocationIds) {
    const location = resolveLocationById(parentLocationId);
    if (!location) {
      continue;
    }

    const page = await fetchFirstPage(parentLocationId);
    if (page.total > 0) {
      return {
        location,
        total: page.total,
        agents: page.records,
      };
    }
  }

  return null;
}
